// ============================================================
// ProjectSurface — shared layout for the per-project sub-routes.
//
// Studio / Site Builder / Hosting all live underneath a project,
// share the same identity header + breadcrumb, and differ only in
// their body content. This component owns the chrome so the
// individual surfaces stay focused on their own behavior.
//
// The surface intentionally does NOT re-render the project nav
// tabs from ProjectPage — sub-routes are entered from the project
// page tile grid and route back via the breadcrumb. Keeping the
// scope tight prevents the shell chrome from sprawling.
// ============================================================

import type { ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useProjects } from '../../hooks/useProjects'
import { SHELL_TOKENS, chipStyle, statusAccent, text } from '../../shell/styles'
import EmptyState from '../../components/EmptyState'

interface ProjectSurfaceProps {
  kicker: string
  title: string
  description?: string
  children: ReactNode
}

export default function ProjectSurface({
  kicker,
  title,
  description,
  children,
}: ProjectSurfaceProps) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { projects, loading } = useProjects()
  const project = projects.find((p) => p.id === id)

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <EmptyState title="Loading project" body="Hydrating from the portfolio." />
      </div>
    )
  }
  if (!project) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <EmptyState
          title="Project not found"
          body="Return to All Projects and pick one."
        />
      </div>
    )
  }

  const accent = statusAccent(project.status)

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <button
        type="button"
        onClick={() => navigate(`/square/projects/${project.id}`)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          borderRadius: 8,
          border: `1px solid ${SHELL_TOKENS.border}`,
          background: SHELL_TOKENS.surface,
          color: SHELL_TOKENS.ink,
          fontSize: 12,
          fontWeight: 600,
          fontFamily: 'var(--font-mono)',
          cursor: 'pointer',
          marginBottom: 16,
          transition: 'border-color 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = SHELL_TOKENS.brandLine
          e.currentTarget.style.background = SHELL_TOKENS.brandSoft
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = SHELL_TOKENS.border
          e.currentTarget.style.background = SHELL_TOKENS.surface
        }}
      >
        <span style={{ fontSize: 14 }}>←</span>
        <span>Back to {project.name}</span>
      </button>

      <div
        style={{
          ...text.kicker,
          fontSize: 10,
          letterSpacing: '0.28em',
          color: SHELL_TOKENS.inkFaint,
          marginBottom: 6,
        }}
      >
        {kicker}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={text.editorial}>{title}</div>
        {project.status && <span style={chipStyle(accent)}>{project.status}</span>}
      </div>
      {description && (
        <div
          style={{
            fontSize: 12,
            color: SHELL_TOKENS.inkMuted,
            marginBottom: 22,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {description}
        </div>
      )}

      {children}
    </div>
  )
}
